import { OfferStatusCode } from "../../serviceOffers/models/models";
import { ServiceRequestStatusCode } from "../../servicing/models/models";

// ═══════════════════════════════════════════════════════════════
//  Guidance Category System — 6 semantic color categories
// ═══════════════════════════════════════════════════════════════

export type GuidanceCategory =
  | "info"
  | "waiting"
  | "action"
  | "success"
  | "terminal"
  | "muted";

/**
 * Color configuration for each guidance category.
 * "info" uses var(--accent) for blue to respect accent theming.
 * All others are fixed semantic colors — urgency should NOT change with accent.
 */
export const guidanceCategoryColors: Record<
  GuidanceCategory,
  { bg: string; border: string; text: string }
> = {
  info: { bg: "#F0F7FF", border: "var(--accent, #3498DB)", text: "#1E293B" },
  waiting: { bg: "#FEF9E7", border: "#F59E0B", text: "#92400E" },
  action: { bg: "#FFF0EB", border: "#F97316", text: "#9A3412" },
  success: { bg: "#ECFDF5", border: "#10B981", text: "#065F46" },
  terminal: { bg: "#FEF2F2", border: "#EF4444", text: "#991B1B" },
  muted: { bg: "#F4F6F9", border: "#9CA3AF", text: "#6B7280" },
};

// ═══════════════════════════════════════════════════════════════
//  Provider perspective (OfferCard)
// ═══════════════════════════════════════════════════════════════

/**
 * Returns the guidance category for a given offer status code.
 * Used by OfferCard to color the context guidance box.
 */
export const getProviderGuidanceCategory = (
  statusCode: number
): GuidanceCategory => {
  const S = OfferStatusCode;
  switch (statusCode) {
    case S.draft:
    case S.sent:
    case S.viewed:
    case S.confirmed:
    case S.workInProgress:
      return "info";
    case S.negotiating:
    case S.workCompleted:
      return "waiting";
    case S.awaitingConfirmation:
      return "action";
    case S.serviceCompleted:
      return "success";
    case S.rejected:
      return "terminal";
    case S.expired:
    case S.withdrawn:
    case S.archived:
      return "muted";
    default:
      return "info";
  }
};

// ═══════════════════════════════════════════════════════════════
//  Owner perspective (ServiceRequestCard)
// ═══════════════════════════════════════════════════════════════

/**
 * Returns the guidance category for a given SR status code.
 * Used by ServiceRequestCard to color the context guidance box.
 *
 * For OpenForOffers, category depends on whether offers exist.
 */
export const getOwnerGuidanceCategory = (
  statusCode: number,
  offersCount?: number
): GuidanceCategory => {
  const S = ServiceRequestStatusCode;
  switch (statusCode) {
    case S.draft:
    case S.negotiating:
    case S.inProgress:
      return "info";
    case S.openForOffers:
      return (offersCount ?? 0) > 0 ? "info" : "waiting";
    case S.awaitingProviderConfirmation:
      return "waiting";
    case S.awaitingCompletionConfirmation:
      return "action";
    case S.completed:
      return "success";
    case S.archived:
      return "muted";
    default:
      return "info";
  }
};
